import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { CordovaService } from 'src/app/core/cordova.service';
import { TaskListCode } from 'src/app/tasks/shared/enums';

import { AdminUser, TaskState, TaskUpdate } from '../models/misc.model';
import { API_URL_PREFIX, BasicService, JSON_CONTENT_TYPE_HEADER, RoutePaths } from './basic.service';
import { ErrorHandlingService } from './error-handling.service';

/**
 * Task-related functionnality that is not specific to either the task list or the task process page
 */
@Injectable({
  providedIn: 'root',
})
export class TaskService {
  taskUpdate$ = new Subject<TaskUpdate>();

  constructor(
    private basicService: BasicService,
    private cordovaService: CordovaService,
    private http: HttpClient,
    private ehs: ErrorHandlingService,
    private alcmeonContext: AlcmeonContextService
  ) {}

  subscribeToPushNotifs() {
    this.cordovaService.pushNotifData$.subscribe(data => {
      // If the notif is clicked (i.e., we were not in the foregound when we received the push notif),
      // and there is a taskId in the payload, let's try to acquire and process that task
      if (!data.foreground && data.taskId) {
        this.processTask(Number(data.taskId), data.taskState || 'pending', null);
      }
    });
  }

  /**
   * id: id of the task that should be acquired and processed
   * currentState: state of that task
   *               (the /acquire endpoint called by AcquireBeforeProcessingGuard makes sure the state hasn't changed)
   * taskListCode: the tab to which the navigation should return when releasing/answering/archiving the task
   *
   * Note that AcquireBeforeProcessingGuard will call acquireTask below before the navigation completes
   */
  processTask(id: number, currentTaskState, taskListCode) {
    const navigationParams = {
      state: {
        taskListCode: taskListCode || TaskListCode.Received,
      },
      // we pass currentTaskState as a query param, as the location state is not yet updated when the guard is called
      queryParams: {
        'current-task-state': currentTaskState,
      },
    };
    this.basicService.navigateForward(`${RoutePaths.TaskProcessPrefix}/${id}`, navigationParams);
  }

  /**
   * Notifies interested parties (only the mobile task list so far) that the logged user just changed the given task's state.
   * Most of these updates will also be triggered by the backend via a push notif,
   * but we want the task list to be updated as soon as possible when we answer or archive a task.
   * TODO remove if it turns out the push notif arrives before we have a chance to call this method
   */
  fireTaskStateUpdateByLoggedUser(taskId: number, state: TaskState) {
    const adminUser = {
      id: this.alcmeonContext.self.id,
      name: '', // will be rendered as 'me' anyway
    };
    this.fireTaskStateUpdate(taskId, state, adminUser);
  }

  fireTaskStateUpdate(taskId: number, state: TaskState, adminUser: AdminUser) {
    this.taskUpdate$.next({
      taskId,
      state: {
        taskStateCode: state,
        adminUser,
      },
    });
  }

  private doAquireTask(acquireURL, taskId) {
    return this.http.post<void>(acquireURL, null, JSON_CONTENT_TYPE_HEADER).pipe(
      tap(response => this.throwIfNotAssignedToMe(taskId, response)),
      tap(() => this.fireTaskStateUpdateByLoggedUser(taskId, TaskState.Assigned)),
      catchError(error => this.handleAcquireTaskError(error))
    );
  }

  acquireTask(taskId, currentState): Observable<void> {
    let urlSuffix = currentState;
    if (currentState === TaskState.OnHold) {
      urlSuffix = 'on_hold';
    }
    if (currentState === TaskState.HelpRequested) {
      urlSuffix = 'help_requested';
    }
    if (currentState === TaskState.HelpAnswered) {
      urlSuffix = 'help_answered';
    }
    if (currentState === TaskState.AutoArchived) {
      urlSuffix = 'archived_auto';
    }
    let acquireURL = `${API_URL_PREFIX}/${this.alcmeonContext.companyId}/task/${taskId}/acquire/${urlSuffix}`;
    if (currentState === TaskState.Assigned) {
      acquireURL = `${API_URL_PREFIX}/${this.alcmeonContext.companyId}/task/${taskId}/steal`;
    }

    return this.doAquireTask(acquireURL, taskId);
  }

  aquireTaskFromAnyState(taskId): Observable<void> {
    const acquireURL = `${API_URL_PREFIX}/${this.alcmeonContext.companyId}/task/${taskId}/acquire/any`;
    return this.doAquireTask(acquireURL, taskId);
  }

  private handleAcquireTaskError(error: HttpErrorResponse): Observable<void> {
    if (this.isCouldNotAcquireError(error)) {
      this.ehs.modalAlertError(error, 'm/tasks-list/error/could-not-acquire-task');
    } else {
      this.ehs.toastAlertUnknownError(error);
    }
    return throwError(error);
  }

  private throwIfNotAssignedToMe(taskId, acquireResponse) {
    let assignedToMe = false;
    try {
      const task = acquireResponse.tasks_by_id[taskId];
      assignedToMe = task.state === 'assigned' && this.alcmeonContext.isMe(task.assigned_to);
    } catch (e) {
      // assignToMe stays false
    }
    if (!assignedToMe) {
      throw this.newCouldNotAcquireError();
    }
  }

  private newCouldNotAcquireError() {
    // create a new one each time because the ehs will set its alreadyProcessed property but nobody will reset it
    return new HttpErrorResponse({ error: 'COULD_NOT_ACQUIRE_ERROR' });
  }

  private isCouldNotAcquireError(httpErrorResponse: HttpErrorResponse) {
    return httpErrorResponse.error === 'COULD_NOT_ACQUIRE_ERROR';
  }
}
